
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { UserData, CalendarEvent, CaseFile, LegalPayment, Note } from '../types';
import { generateDailyBriefing } from '../services/geminiService';
import { isSupabaseConfigured, checkSupabaseHealth } from '../services/supabaseClient';

interface HomeOverviewProps {
  user: UserData;
  events: CalendarEvent[];
  cases: CaseFile[];
  payments: LegalPayment[];
  notes: Note[];
}

const HomeOverview: React.FC<HomeOverviewProps> = ({ user, events, cases, payments, notes }) => {
  const navigate = useNavigate();
  const [briefing, setBriefing] = useState<string>('');
  const [isLoadingBriefing, setIsLoadingBriefing] = useState(true);
  const [dbStatus, setDbStatus] = useState<'checking' | 'online' | 'offline'>('checking');
  const [latency, setLatency] = useState<number | null>(null);

  const fetchBriefing = useCallback(async () => {
    setIsLoadingBriefing(true);
    try {
      const msg = await generateDailyBriefing(events, cases);
      setBriefing(msg);
    } catch (err) {
      setBriefing("Sistemul AI este online pe serverul LexSmart. Analizez datele zilei...");
    } finally {
      setIsLoadingBriefing(false);
    }
  }, [events, cases]);

  const verifyConnectivity = useCallback(async () => {
    const start = performance.now();
    const isUp = await checkSupabaseHealth();
    const end = performance.now();
    setLatency(Math.round(end - start));
    setDbStatus(isUp ? 'online' : 'offline');
  }, []);

  useEffect(() => {
    fetchBriefing();
    verifyConnectivity();
    const interval = setInterval(verifyConnectivity, 30000);
    return () => clearInterval(interval);
  }, [fetchBriefing, verifyConnectivity]);

  const updatedCases = cases.filter(c => c.hasPortalUpdate);
  const todayEvents = events.filter(e => new Date(e.date).toDateString() === new Date().toDateString());
  const currentDomain = window.location.hostname;

  return (
    <div className="w-full px-4 md:px-10 space-y-6 md:space-y-8 animate-in fade-in duration-1000 max-w-7xl mx-auto pb-24 md:pb-20 pt-4 md:pt-6">
      
      {/* Control Center - Enhanced with Real Status */}
      <div className="bg-[#0f172a] rounded-[2rem] md:rounded-[3.5rem] p-6 md:p-16 text-white shadow-[0_20px_60px_-15px_rgba(0,0,0,0.5)] relative overflow-hidden border border-white/10">
        <div className="relative z-10 grid lg:grid-cols-12 gap-8 md:gap-10 items-center">
          
          <div className="lg:col-span-7 space-y-6 md:space-y-8">
            <div className="flex items-center gap-3">
              <div className="flex h-3 w-3 relative">
                <span className={`animate-ping absolute inline-flex h-full w-full rounded-full opacity-75 ${dbStatus === 'online' ? 'bg-emerald-400' : 'bg-rose-400'}`}></span>
                <span className={`relative inline-flex rounded-full h-3 w-3 ${dbStatus === 'online' ? 'bg-emerald-500' : 'bg-rose-500'}`}></span>
              </div>
              <h3 className="font-black italic text-[9px] md:text-[11px] uppercase tracking-[0.4em] text-emerald-400">
                {dbStatus === 'online' ? 'SISTEM LIVE' : 'RECONECTARE...'} • {currentDomain}
              </h3>
            </div>

            <div className="space-y-2 md:space-y-4">
              <h2 className="text-4xl md:text-7xl font-black italic tracking-tighter leading-[0.9] uppercase">
                Control <br/>
                <span className="text-indigo-500">Center</span>
              </h2>
              <p className="text-indigo-300/40 font-black text-[10px] md:text-sm uppercase tracking-[0.5em] italic">LexSmart Pro Suite</p>
            </div>

            <div className="flex flex-wrap gap-3 pt-2 md:pt-4">
               <div className="bg-white/5 backdrop-blur-md border border-white/10 px-4 md:px-5 py-2 md:py-3 rounded-2xl flex items-center gap-3 group hover:bg-white/10 transition-all cursor-default">
                  <span className="text-base md:text-lg">📂</span>
                  <span className="text-[9px] md:text-[10px] font-black uppercase tracking-widest text-white/70">{cases.length} Dosare</span>
               </div>
               <div className="bg-white/5 backdrop-blur-md border border-white/10 px-4 md:px-5 py-2 md:py-3 rounded-2xl flex items-center gap-3 group hover:bg-white/10 transition-all cursor-default">
                  <span className="text-base md:text-lg">⚡</span>
                  <span className="text-[9px] md:text-[10px] font-black uppercase tracking-widest text-white/70">{latency || '--'}ms RTT</span>
               </div>
            </div>
          </div>
          
          <div className="lg:col-span-5 mt-4 lg:mt-0">
            <div className="bg-gradient-to-br from-indigo-600/20 to-indigo-900/40 backdrop-blur-3xl border border-white/10 p-6 md:p-10 rounded-[2rem] md:rounded-[3rem] shadow-[inset_0_0_40px_rgba(255,255,255,0.05)] relative overflow-hidden">
               <div className="flex justify-between items-start mb-6 md:mb-10">
                 <div>
                   <p className="text-[8px] md:text-[9px] font-black uppercase tracking-[0.3em] text-indigo-300/60 mb-1 italic">Status Infrastructură</p>
                   <h4 className={`text-xl md:text-3xl font-black italic tracking-tighter uppercase ${dbStatus === 'online' ? 'text-white' : 'text-rose-400'}`}>
                    {dbStatus === 'online' ? 'Host Stabil' : 'Eroare Host'}
                   </h4>
                 </div>
                 <span className={`text-xl md:text-2xl ${dbStatus === 'online' ? 'animate-pulse' : 'grayscale opacity-20'}`}>🛰️</span>
               </div>

               <div className="space-y-4 md:space-y-6">
                 <div className="flex justify-between items-end">
                    <span className="text-[9px] md:text-[10px] font-black text-indigo-400 uppercase tracking-widest italic">Integritate Securitate</span>
                    <span className="text-xl md:text-2xl font-black text-white italic">100<span className="text-xs opacity-40 ml-0.5">%</span></span>
                 </div>
                 <div className="w-full bg-white/5 h-2.5 rounded-full overflow-hidden border border-white/5">
                    <div className={`h-full transition-all duration-1000 shadow-[0_0_20px_rgba(16,185,129,0.4)] ${dbStatus === 'online' ? 'w-full bg-gradient-to-r from-emerald-500 to-indigo-500' : 'w-0 bg-rose-500'}`}></div>
                 </div>
                 <div className="pt-2 md:pt-4 flex justify-between items-center text-[7px] md:text-[8px] font-black text-white/20 uppercase tracking-[0.3em]">
                    <span>SSL: ACTIV</span>
                    <span>DB: {dbStatus.toUpperCase()}</span>
                 </div>
               </div>
               
               <div className={`absolute -right-10 -bottom-10 w-32 h-32 rounded-full blur-[40px] ${dbStatus === 'online' ? 'bg-indigo-500/20' : 'bg-rose-500/10'}`}></div>
            </div>
          </div>
        </div>
        
        <div className="absolute right-0 top-0 w-[50%] h-full bg-indigo-600/5 rounded-full blur-[120px] -z-10"></div>
        <div className="hidden md:block absolute top-0 right-0 p-8 text-[120px] font-black italic text-white/[0.03] leading-none select-none pointer-events-none">PRO</div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
        <div className="lg:col-span-8 space-y-8">
          <div className="bg-white rounded-[3rem] p-10 md:p-12 border border-slate-100 shadow-xl relative overflow-hidden group hover:border-indigo-200 transition-all">
            <div className="flex justify-between items-center mb-10">
              <div className="flex items-center gap-4">
                 <div className="w-12 h-12 bg-slate-900 rounded-2xl flex items-center justify-center text-white text-xl shadow-lg">✨</div>
                 <div>
                   <span className="text-[10px] font-black text-slate-900 uppercase tracking-widest italic">Briefing Senior Partner</span>
                   <p className="text-[8px] text-slate-400 font-bold uppercase tracking-widest">Generat prin Gemini AI</p>
                 </div>
              </div>
              <button onClick={fetchBriefing} className="w-10 h-10 flex items-center justify-center bg-slate-50 rounded-xl text-slate-300 hover:text-indigo-600 transition-all border border-slate-100 shadow-sm">
                🔄
              </button>
            </div>
            <div className="min-h-[100px]">
              {isLoadingBriefing ? (
                <div className="space-y-4 animate-pulse">
                  <div className="h-4 bg-slate-50 rounded-full w-full"></div>
                  <div className="h-4 bg-slate-50 rounded-full w-3/4"></div>
                </div>
              ) : (
                <p className="text-2xl md:text-3xl font-medium text-slate-800 italic leading-relaxed tracking-tight">
                  "{briefing}"
                </p>
              )}
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
             <div className="bg-white rounded-[2.5rem] p-10 border border-slate-50 shadow-lg group hover:bg-slate-50/50 transition-colors cursor-pointer" onClick={() => navigate('/dashboard/calendar')}>
                <div className="flex justify-between items-center mb-8">
                  <h4 className="text-[10px] font-black text-slate-400 uppercase tracking-widest italic">Ședințe Astăzi</h4>
                  <span className="text-xl">📅</span>
                </div>
                <div className="space-y-4">
                  {todayEvents.length === 0 ? (
                    <div className="py-10 text-center border-2 border-dashed border-slate-100 rounded-3xl">
                       <p className="text-slate-300 text-[10px] font-black uppercase tracking-widest italic">Program liber azi</p>
                    </div>
                  ) : (
                    todayEvents.map(e => (
                      <div key={e.id} className="flex items-center justify-between p-5 bg-white border border-slate-100 rounded-2xl shadow-sm hover:shadow-md transition-shadow">
                        <span className="text-indigo-600 font-black italic text-sm">{new Date(e.date).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}</span>
                        <span className="text-[11px] font-black text-slate-700 truncate max-w-[140px] italic">{e.title}</span>
                      </div>
                    ))
                  )}
                </div>
             </div>

             <div className="bg-white rounded-[2.5rem] p-10 border border-slate-50 shadow-lg group hover:bg-slate-50/50 transition-colors cursor-pointer" onClick={() => navigate('/dashboard')}>
                <div className="flex justify-between items-center mb-8">
                  <h4 className="text-[10px] font-black text-slate-400 uppercase tracking-widest italic">Radar Portal Just</h4>
                  <span className="text-xl">🏛️</span>
                </div>
                <div className="space-y-4">
                  {updatedCases.length === 0 ? (
                    <div className="py-10 text-center border-2 border-dashed border-slate-100 rounded-3xl">
                       <p className="text-slate-300 text-[10px] font-black uppercase tracking-widest italic">Monitorizare pasivă...</p>
                    </div>
                  ) : (
                    updatedCases.slice(0, 3).map(c => (
                      <div key={c.id} className="p-5 bg-emerald-50 rounded-2xl border border-emerald-100 flex flex-col gap-3 shadow-sm">
                        <div className="flex justify-between items-center">
                          <span className="text-[10px] font-black text-emerald-800 italic">{c.caseNumber}</span>
                          <span className="text-[9px] bg-emerald-500 text-white px-3 py-1.5 rounded-full font-black uppercase tracking-widest">NOU</span>
                        </div>
                        <span className="self-start text-[8px] font-black px-2 py-1 rounded bg-white/60 text-emerald-700 uppercase border border-emerald-200/50">{c.phase || 'Fond'}</span>
                      </div>
                    ))
                  )}
                </div>
             </div>
          </div>
        </div>

        <div className="lg:col-span-4 space-y-8">
           <div className="bg-indigo-600 rounded-[3.5rem] p-12 text-white shadow-2xl shadow-indigo-100 relative overflow-hidden group">
              <div className="relative z-10">
                <p className="text-[11px] font-black uppercase tracking-widest opacity-60 mb-2 italic">Onorarii Scadente</p>
                <div className="flex items-baseline gap-2">
                  <h4 className="text-6xl font-black italic tracking-tighter">
                    {payments.filter(p => p.direction === 'incasare' && p.status === 'pending').reduce((a, b) => a + b.amount, 0).toLocaleString()}
                  </h4>
                  <span className="text-sm font-black opacity-40">RON</span>
                </div>
                <button 
                  onClick={() => navigate('/dashboard/finance')} 
                  className="w-full mt-12 py-6 bg-white text-indigo-600 rounded-[2rem] font-black text-[11px] uppercase tracking-[0.25em] shadow-xl hover:translate-y-[-4px] transition-all active:scale-95"
                >
                  FINANȚE →
                </button>
              </div>
              <div className="absolute -right-10 -bottom-10 text-[140px] opacity-10 font-black italic select-none">RON</div>
           </div>

           <div className="bg-white rounded-[3.5rem] p-12 border border-slate-100 shadow-xl space-y-10">
              <div className="flex items-center justify-between">
                <h4 className="text-[10px] font-black text-slate-900 uppercase tracking-widest italic">Server Health</h4>
                <div className={`w-2.5 h-2.5 rounded-full animate-pulse ${dbStatus === 'online' ? 'bg-emerald-500 shadow-[0_0_10px_#10b981]' : 'bg-rose-500 shadow-[0_0_10px_#f43f5e]'}`}></div>
              </div>
              <div className="space-y-8">
                 <div className="flex justify-between items-center pb-6 border-b border-slate-50">
                    <div className="flex flex-col">
                      <span className="text-sm font-black text-slate-800 italic">Data Center</span>
                      <span className="text-[9px] text-slate-400 font-bold uppercase tracking-widest">Supabase Engine</span>
                    </div>
                    <span className={`text-[10px] font-black uppercase italic ${dbStatus === 'online' ? 'text-emerald-500' : 'text-rose-500'}`}>
                      {dbStatus === 'online' ? 'CONECTAT' : 'OFFLINE'}
                    </span>
                 </div>
                 <div className="flex justify-between items-center pb-6 border-b border-slate-50">
                    <div className="flex flex-col">
                      <span className="text-sm font-black text-slate-800 italic">Latency Test</span>
                      <span className="text-[9px] text-slate-400 font-bold uppercase tracking-widest">Round-trip Time</span>
                    </div>
                    <span className="text-[10px] font-black text-indigo-500 uppercase italic">{latency ? `${latency}ms` : '---'}</span>
                 </div>
              </div>
           </div>
        </div>
      </div>
    </div>
  );
};

export default HomeOverview;
